import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';

import {
  Unit,
  Lease,
} from '@urbanmix-tech/shared-js';
import { deleteLease } from '@/data';

import { convertPseudoDateToRealDate } from '@/helpers/common';
import {
  findAllLeaseConflicts,
  getLeaseCategory,
  getCategoryColor,
  getCategoryLabel,
  type LeaseConflict,
} from '@/helpers/leaseHelpers';
import { useLoading } from '@/shared/context/UIContextProvider';

// Redux
import { useDispatch } from 'react-redux';
import {
  setSelectedUnit,
  setBuildingVisual,
  setHighlightedUnits,
} from '@/features/project/projectSlice';

import { LeaseCard } from './LeaseCard';

type LeaseConflictsTabContentProps = {
  units: Unit[];
  leases?: Lease[];
  hoveredUnitId: string | null;
  onSelectUnit(unitId: string | null): void;
  onHoverUnit(unitId: string | null): void;
  onLeaseUpdated?: (lease: Lease) => void;
  onLeaseDeleted?: (leaseId: string) => void;
};

export function LeaseConflictsTabContent({
  units,
  leases,
  hoveredUnitId,
  onSelectUnit,
  onHoverUnit,
  onLeaseUpdated,
  onLeaseDeleted,
}: LeaseConflictsTabContentProps) {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { createLoading } = useLoading();

  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set());

  // Create a map of unit ID to unit for quick lookup
  const unitMap = useMemo(() => {
    const map = new Map<string, Unit>();
    units.forEach((unit) => map.set(unit.id, unit));
    return map;
  }, [units]);

  // Find all lease conflicts
  const conflicts = useMemo(() => {
    if (!leases) return [];
    return findAllLeaseConflicts(leases);
  }, [leases]);

  // Sort conflicts by number of conflict pairs (most conflicts first)
  const sortedConflicts = useMemo(() => {
    return [...conflicts].sort((a, b) => b.conflictPairs.length - a.conflictPairs.length);
  }, [conflicts]);

  // Get total counts
  const totalConflictingUnits = conflicts.length;
  const totalConflictPairs = conflicts.reduce((sum, c) => sum + c.conflictPairs.length, 0);

  // Update building visualization to highlight conflicting units
  useEffect(() => {
    if (conflicts.length === 0) {
      dispatch(
        setBuildingVisual({
          apartmentData: [],
          visualizer: null,
        })
      );
      return;
    }

    // Create visualization data showing conflict severity
    const apartmentData = conflicts.map((conflict) => ({
      apartmentId: conflict.unitId,
      value: conflict.conflictPairs.length,
      label: `${conflict.conflictPairs.length} conflict(s)`,
    }));

    dispatch(
      setBuildingVisual({
        apartmentData,
        visualizer: null,
      })
    );

    // Highlight all conflicting units
    setTimeout(() => {
      dispatch(setHighlightedUnits(apartmentData));
    }, 100);
  }, [conflicts, dispatch]);

  // Handle delete lease
  const handleDeleteLease = async (leaseId: string) => {
    const loader = createLoading();
    loader.startLoading({ title: 'Deleting lease...' });

    try {
      await deleteLease(leaseId);
      onLeaseDeleted?.(leaseId);
    } catch (error) {
      console.error('Error deleting lease:', error);
    } finally {
      loader.stopLoading();
    }
  };

  // Toggle unit expansion
  const toggleUnit = (unitId: string) => {
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
  };

  // Expand all
  const expandAll = () => {
    setExpandedUnits(new Set(conflicts.map((c) => c.unitId)));
  };

  // Collapse all
  const collapseAll = () => {
    setExpandedUnits(new Set());
  };

  // Format date for display
  const formatDate = (date: Date | undefined | any) => {
    const realDate = convertPseudoDateToRealDate(date);
    if (!realDate) return 'N/A';
    return dayjs(realDate).format('MMM D, YYYY');
  };

  return (
    <div className="lease-conflicts h-[calc(100vh-112px)] w-full flex flex-col p-[15px] overflow-hidden" ref={ref}>
      {/* Header */}
      <Box className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <WarningAmberIcon color="warning" />
          <Typography variant="h6">
            Lease Conflicts
          </Typography>
          {totalConflictingUnits > 0 && (
            <Chip
              label={`${totalConflictingUnits} unit${totalConflictingUnits !== 1 ? 's' : ''} with conflicts`}
              color="warning"
              size="small"
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          {conflicts.length > 0 && (
            <>
              <Button size="small" onClick={expandAll}>
                Expand All
              </Button>
              <Button size="small" onClick={collapseAll}>
                Collapse All
              </Button>
            </>
          )}
        </div>
      </Box>

      {/* Summary Alert */}
      {totalConflictingUnits > 0 ? (
        <Alert severity="warning" className="mb-4">
          Found <strong>{totalConflictPairs}</strong> overlapping lease pair{totalConflictPairs !== 1 ? 's' : ''} across{' '}
          <strong>{totalConflictingUnits}</strong> unit{totalConflictingUnits !== 1 ? 's' : ''}.
          Review and resolve conflicts by editing lease dates or deleting duplicate leases.
        </Alert>
      ) : (
        <Alert severity="success" className="mb-4">
          No lease conflicts found. All leases have non-overlapping date ranges.
        </Alert>
      )}

      {/* Conflict List */}
      <div className="flex-1 overflow-y-auto flex flex-col gap-4">
        {sortedConflicts.map((conflict) => {
          const unit = unitMap.get(conflict.unitId);
          if (!unit) return null;

          const isExpanded = expandedUnits.has(conflict.unitId);

          return (
            <Accordion
              key={conflict.unitId}
              expanded={isExpanded}
              onChange={() => toggleUnit(conflict.unitId)}
              sx={{
                bgcolor: 'rgba(255, 167, 38, 0.05)',
                border: '1px solid rgba(255, 167, 38, 0.3)',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ '&:hover': { bgcolor: 'rgba(255, 167, 38, 0.1)' } }}
              >
                <div className="flex items-center gap-4 w-full">
                  <Typography variant="subtitle1" className="font-semibold">
                    Unit {unit.name || unit.id}
                  </Typography>
                  <Chip
                    label={`${conflict.conflictPairs.length} overlap${conflict.conflictPairs.length !== 1 ? 's' : ''}`}
                    color="warning"
                    size="small"
                  />
                  <Typography variant="body2" color="textSecondary" className="ml-auto mr-4">
                    {unit.numBedrooms} BR | {unit.sqft} sqft
                  </Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <Stack gap={3}>
                  {/* Conflict Details */}
                  <Box sx={{ bgcolor: 'rgba(255, 167, 38, 0.1)', p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" className="mb-2 font-semibold">
                      Overlapping Periods:
                    </Typography>
                    {conflict.conflictPairs.map((pair, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2 text-sm">
                        <Chip
                          label={getCategoryLabel(getLeaseCategory(pair.lease1))}
                          size="small"
                          sx={{
                            bgcolor: getCategoryColor(getLeaseCategory(pair.lease1)),
                            color: 'white',
                            fontSize: '0.7rem',
                          }}
                        />
                        <span>
                          {formatDate(pair.lease1.leaseStartDate)} - {formatDate(pair.lease1.leaseEndDate)}
                        </span>
                        <span className="text-warning-main font-semibold">overlaps with</span>
                        <Chip
                          label={getCategoryLabel(getLeaseCategory(pair.lease2))}
                          size="small"
                          sx={{
                            bgcolor: getCategoryColor(getLeaseCategory(pair.lease2)),
                            color: 'white',
                            fontSize: '0.7rem',
                          }}
                        />
                        <span>
                          {formatDate(pair.lease2.leaseStartDate)} - {formatDate(pair.lease2.leaseEndDate)}
                        </span>
                        <Chip
                          label={`${pair.overlapDays} day${pair.overlapDays !== 1 ? 's' : ''}`}
                          size="small"
                          color="error"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </div>
                    ))}
                  </Box>

                  {/* Lease Cards */}
                  <Typography variant="subtitle2" className="font-semibold">
                    Conflicting Leases ({conflict.leases.length}):
                  </Typography>
                  <Stack gap={2}>
                    {conflict.leases
                      .sort((a, b) => {
                        const dateA = convertPseudoDateToRealDate(a.leaseStartDate as any);
                        const dateB = convertPseudoDateToRealDate(b.leaseStartDate as any);
                        return (dateA?.getTime() || 0) - (dateB?.getTime() || 0);
                      })
                      .map((lease) => (
                        <LeaseCard
                          key={lease.id}
                          lease={lease}
                          unit={unit}
                          processes={[]}
                          isHovered={hoveredUnitId === unit.id}
                          onHoverUnit={onHoverUnit}
                          onSelectUnit={onSelectUnit}
                          showEdit={true}
                          showCategory={true}
                          borderColorOverride="#ff9800"
                          onLeaseUpdated={onLeaseUpdated}
                          onDelete={handleDeleteLease}
                        />
                      ))}
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {conflicts.length === 0 && (
          <div className="flex items-center justify-center flex-1">
            <Typography variant="h6" color="textSecondary">
              No lease conflicts detected
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}
